/**
 * Step components for the create-project wizard.
 *
 * Each Step* is pure presentation: it reads `state` + `errors` from the
 * parent and pushes updates via `setState`. No fetching, no validation —
 * the parent runs the schemas (see ./schema.ts) and feeds the errors back.
 */
import {
  Input,
} from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { IconPicker } from '@/components/shared/icon-picker'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import { PROJECT_ICONS } from '@/lib/utils/lucide-icon-catalog'
import type { ProjectMemberRole, ExtraMember } from '../../api/create-project'
import type { WizardState } from './types'
import { UserPicker } from './user-picker'

export interface StepProps {
  state: WizardState
  setState: (patch: Partial<WizardState>) => void
  errors: Record<string, string>
}

export const STATUS_LABELS: Record<WizardState['status'], string> = {
  active: 'فعال',
  paused: 'متوقف',
  closed: 'بسته',
  archived: 'بایگانی',
}

export const ROLE_LABELS: Record<ProjectMemberRole, string> = {
  admin: 'ادمین',
  data_entry: 'ورود داده',
  approver: 'تأییدکننده',
  viewer: 'بیننده',
}

function Field({
  label, required, error, children,
}: {
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

export function StepIdentity({ state, setState, errors }: StepProps) {
  return (
    <div className="space-y-4 py-2">
      <Field label="نام پروژه" required error={errors.name}>
        <Input
          value={state.name}
          onChange={(e) => setState({ name: e.target.value })}
          placeholder="مثال: رجیستری سرطان"
        />
      </Field>

      <Field label="کد پروژه" error={errors.code}>
        <Input
          value={state.code}
          onChange={(e) => setState({ code: e.target.value.toUpperCase() })}
          placeholder="مثال: CANREG"
          className="font-mono"
        />
      </Field>

      <Field label="آیکون" error={errors.icon}>
        <IconPicker
          value={state.icon}
          onChange={(key) => setState({ icon: key })}
          icons={PROJECT_ICONS}
          color={state.color}
        />
      </Field>

      <Field label="رنگ" error={errors.color}>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={state.color}
            onChange={(e) => setState({ color: e.target.value })}
            className="h-10 w-12 cursor-pointer rounded border border-border bg-transparent p-0.5"
          />
          <Input
            value={state.color}
            onChange={(e) => setState({ color: e.target.value })}
            className="font-mono"
          />
        </div>
      </Field>
    </div>
  )
}

export function StepMetadata({ state, setState, errors }: StepProps) {
  return (
    <div className="space-y-4 py-2">
      <Field label="توضیحات" error={errors.description}>
        <Textarea
          value={state.description}
          onChange={(e) => setState({ description: e.target.value })}
          rows={3}
          placeholder="هدف پروژه و دامنه کار..."
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="دپارتمان" error={errors.department}>
          <Input value={state.department} onChange={(e) => setState({ department: e.target.value })} placeholder="مثال: آنکولوژی" />
        </Field>
        <Field label="پژوهشگر مسئول" error={errors.principal_investigator}>
          <Input value={state.principal_investigator} onChange={(e) => setState({ principal_investigator: e.target.value })} placeholder="نام و نام خانوادگی" />
        </Field>
        <Field label="مؤسسه" error={errors.institution}>
          <Input value={state.institution} onChange={(e) => setState({ institution: e.target.value })} />
        </Field>
        <Field label="شماره IRB / کمیته اخلاق" error={errors.irb_reference}>
          <Input value={state.irb_reference} onChange={(e) => setState({ irb_reference: e.target.value })} />
        </Field>
      </div>
    </div>
  )
}

export function StepSchedule({ state, setState, errors }: StepProps) {
  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="تاریخ شروع" error={errors.start_date}>
          <Input type="date" value={state.start_date} onChange={(e) => setState({ start_date: e.target.value })} />
        </Field>
        <Field label="تاریخ پایان" error={errors.end_date}>
          <Input type="date" value={state.end_date} onChange={(e) => setState({ end_date: e.target.value })} />
        </Field>
        <Field label="منطقه زمانی" error={errors.time_zone}>
          <Input value={state.time_zone} onChange={(e) => setState({ time_zone: e.target.value })} />
        </Field>
        <Field label="زبان پیش‌فرض" error={errors.default_language}>
          <Select value={state.default_language} onValueChange={(v) => setState({ default_language: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fa">فارسی</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="تقویم پیش‌فرض" error={errors.default_calendar}>
          <Select value={state.default_calendar} onValueChange={(v) => setState({ default_calendar: v as 'jalali' | 'gregorian' })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="jalali">جلالی</SelectItem>
              <SelectItem value="gregorian">میلادی</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="وضعیت اولیه" error={errors.status}>
          <Select value={state.status} onValueChange={(v) => setState({ status: v as WizardState['status'] })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="سیاست نگهداری داده" error={errors.data_retention_policy}>
        <Textarea value={state.data_retention_policy} onChange={(e) => setState({ data_retention_policy: e.target.value })} rows={2} placeholder="مثال: ۵ سال نگهداری مطابق الزامات قانونی" />
      </Field>
    </div>
  )
}

interface StepMembersProps extends StepProps {
  isSuperUser: boolean
  excludedFromExtras: string[]
}

export function StepMembers({
  state, setState, errors, isSuperUser, excludedFromExtras,
}: StepMembersProps) {
  const addRow = () =>
    setState({
      extra_members: [
        ...state.extra_members,
        { user_id: '', role: 'data_entry' as ProjectMemberRole },
      ],
    })
  const updateRow = (idx: number, patch: Partial<ExtraMember>) => {
    const next = state.extra_members.map((m, i) => (i === idx ? { ...m, ...patch } : m))
    setState({ extra_members: next })
  }
  const removeRow = (idx: number) => {
    setState({ extra_members: state.extra_members.filter((_, i) => i !== idx) })
  }

  return (
    <div className="space-y-5 py-2">
      <div>
        <Field label="ادمین اصلی پروژه" required error={errors.initial_admin_id}>
          {isSuperUser ? (
            <UserPicker
              value={state.initial_admin_id || null}
              onChange={(id) => setState({ initial_admin_id: id ?? '' })}
              placeholder="انتخاب ادمین..."
            />
          ) : (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              شما (کاربر فعلی) به‌عنوان ادمین اصلی تنظیم می‌شوید.
            </div>
          )}
        </Field>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">اعضای اضافی</h4>
          <Button type="button" variant="outline" size="sm" onClick={addRow}>
            افزودن عضو
          </Button>
        </div>

        {state.extra_members.length === 0 ? (
          <p className="text-xs text-muted-foreground">عضوی اضافه نشده است.</p>
        ) : (
          <div className="space-y-2">
            {state.extra_members.map((m, idx) => {
              const others = excludedFromExtras.filter((id) => id && id !== m.user_id)
              return (
                <div
                  key={idx}
                  className="grid grid-cols-1 gap-2 rounded-md border border-border p-2 sm:grid-cols-[1fr_180px_auto]"
                >
                  <UserPicker
                    value={m.user_id || null}
                    onChange={(id) => updateRow(idx, { user_id: id ?? '' })}
                    excludeIds={others}
                  />
                  <Select value={m.role} onValueChange={(v) => updateRow(idx, { role: v as ProjectMemberRole })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(idx)}>
                    حذف
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export function StepReview({
  state, isSuperUser,
}: { state: WizardState; isSuperUser: boolean }) {
  const rows: Array<[string, string]> = [
    ['نام', state.name || '—'],
    ['کد', state.code || '—'],
    ['دپارتمان', state.department || '—'],
    ['پژوهشگر', state.principal_investigator || '—'],
    ['مؤسسه', state.institution || '—'],
    ['IRB', state.irb_reference || '—'],
    ['تاریخ شروع', state.start_date || '—'],
    ['تاریخ پایان', state.end_date || '—'],
    ['منطقه زمانی', state.time_zone],
    ['زبان', state.default_language],
    ['تقویم', state.default_calendar === 'jalali' ? 'جلالی' : 'میلادی'],
    ['وضعیت', STATUS_LABELS[state.status]],
  ]
  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center gap-3 rounded-md border border-border p-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-white"
          style={{ backgroundColor: state.color }}
        >
          {state.icon && <DynamicIcon name={state.icon} className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{state.name || '—'}</p>
          {state.code && <p className="font-mono text-xs text-muted-foreground">{state.code}</p>}
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 border-b border-border/50 py-1">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="truncate text-foreground">{v}</dd>
          </div>
        ))}
      </dl>

      {state.description && (
        <div className="rounded-md bg-muted/40 p-3 text-sm">
          <p className="mb-1 text-xs font-medium text-muted-foreground">توضیحات</p>
          <p className="whitespace-pre-wrap">{state.description}</p>
        </div>
      )}

      <div className="rounded-md border border-border p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">اعضا</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span>ادمین اصلی</span>
            <span className="font-mono text-xs">
              {isSuperUser ? state.initial_admin_id || '—' : 'شما'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>اعضای اضافی</span>
            <span>{state.extra_members.length} نفر</span>
          </div>
        </div>
      </div>
    </div>
  )
}
